/**
 * Idiomas de la interfaz. Los mismos tres que `web/consumer`.
 *
 * Ojo con la distinción, que es la que hace que este dashboard esté bien
 * internacionalizado y no a medias:
 *
 *   **idioma** (`Language`) → qué textos se muestran. Lo elige la persona.
 *   **locale** (`TenantSettings.locale`) → cómo se formatean fechas, números
 *                                          y moneda. Lo fija el tenant.
 *
 * Un analista brasileño de un laboratorio paraguayo puede leer la interfaz en
 * portugués y seguir viendo las fechas como las escribe el laboratorio.
 */
export const LANGUAGES = ['es', 'en', 'pt'] as const;

export type Language = (typeof LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'es';

/** Nombre de cada idioma en su propio idioma, para el selector. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  es: 'Español',
  en: 'English',
  pt: 'Português',
};

export function isLanguage(value: unknown): value is Language {
  return value === 'es' || value === 'en' || value === 'pt';
}

/** `pt-BR` → `pt`, `ES_py` → `es`, basura → `null`. */
export function coerceLanguage(tag: unknown): Language | null {
  if (typeof tag !== 'string') return null;
  const base = tag.trim().toLowerCase().replace('_', '-').split('-')[0];
  return isLanguage(base) ? base : null;
}

/** Las tres fuentes de las que puede salir el idioma de la interfaz. */
export interface LanguageSources {
  /** Lo que la persona eligió en el selector. Persistido en `localStorage`. */
  override?: Language | null;
  /**
   * El idioma del tenant de la sesión. `null` mientras no se sepa cuál es —
   * o sea, antes de iniciar sesión y mientras carga su configuración.
   */
  tenant?: Language | null;
  /** Primer idioma soportado de `navigator.languages`. */
  browser?: Language | null;
}

/**
 * Resuelve el idioma de la interfaz. **Este es el orden y no hay otro:**
 *
 *   1. la elección explícita de la persona,
 *   2. el idioma configurado por la marca,
 *   3. el del navegador,
 *   4. español.
 *
 * El navegador queda tercero y en la práctica sólo decide **antes de iniciar
 * sesión**, que es cuando todavía no se sabe de qué marca es la persona: la
 * pantalla de login sale en su idioma en vez de siempre en español. Con la
 * sesión abierta manda la marca, salvo que la persona diga otra cosa.
 *
 * Ojo con la distinción que hace que esto esté bien internacionalizado y no a
 * medias: esto decide **qué textos** se muestran. **Cómo se formatean** las
 * fechas y los números no se elige nunca — es el locale del tenant. Ver
 * `tenant/settings.ts`.
 */
export function resolveLanguage(sources: LanguageSources): Language {
  return sources.override ?? sources.tenant ?? sources.browser ?? DEFAULT_LANGUAGE;
}

/** Primer idioma soportado que declare el navegador, o `null`. */
export function browserLanguage(): Language | null {
  if (typeof navigator === 'undefined') return null;
  for (const tag of navigator.languages) {
    const language = coerceLanguage(tag);
    if (language !== null) return language;
  }
  return null;
}
